import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HeartCrack, Flame, Check, Lock, Lightbulb, ArrowRight, RotateCcw, MessageCircle, AlertTriangle, Target } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const PHASES = [
  { label: "Grief & Space", range: [1, 7], color: "from-rose-500/20 to-transparent" },
  { label: "Reset", range: [8, 14], color: "from-amber-500/20 to-transparent" },
  { label: "Rebuild", range: [15, 21], color: "from-emerald-500/20 to-transparent" },
  { label: "Reach-Out Prep", range: [22, 30], color: "from-sky-500/20 to-transparent" },
];

const ExRecovery = () => {
  const [data, setData] = useState(null);
  const [context, setContext] = useState("");
  const [length, setLength] = useState("");
  const [starting, setStarting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = async () => {
    const r = await api.get("/ex-recovery");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/ex-recovery/start", { context, length_of_relationship: length });
      toast.success("Day 1 begins now. Stay the course.");
      await load();
    } catch { toast.error("Could not start program"); }
    setStarting(false);
  };

  const toggleDay = async (day, nowDone) => {
    try {
      const r = await api.post("/ex-recovery/complete-day", { day, completed: nowDone });
      setData((d) => ({ ...d, completed_days: r.data.completed_days }));
      if (nowDone) toast.success(`Day ${day} marked done.`);
    } catch { toast.error("Could not save"); }
  };

  const reset = async () => {
    try {
      await api.post("/ex-recovery/reset");
      toast("Program reset. Ready when you are.");
      setConfirmReset(false);
      await load();
    } catch { toast.error("Could not reset"); }
  };

  if (!data) return <div className="p-10 text-neutral-500" data-testid="recovery-loading">Loading…</div>;

  if (!data.active) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-start gap-4 mb-6">
          <HeartCrack className="w-8 h-8 text-amber-500 shrink-0 mt-1" />
          <div>
            <div className="overline mb-1">BREAKUP RECOVERY</div>
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight" data-testid="recovery-title">
              The <span className="italic text-amber-500">30-Day No-Contact</span> Program
            </h1>
            <p className="text-neutral-400 mt-4 max-w-xl leading-relaxed italic">
              Not a strategy to make her miss you. An operating system for the healing you cannot skip — and the man you can become in 30 days.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mt-8" data-testid="phases-overview">
          {PHASES.map((p) => (
            <div key={p.label} className={`card-surface p-5 bg-gradient-to-b ${p.color}`}>
              <div className="overline text-neutral-500 mb-1">DAYS {p.range[0]}-{p.range[1]}</div>
              <div className="font-serif text-lg">{p.label}</div>
            </div>
          ))}
        </div>

        <div className="card-surface p-8 mt-8">
          <h2 className="font-serif text-2xl mb-3">Before you start — the rules</h2>
          <ul className="space-y-2 text-neutral-300">
            {[
              "No texts, no calls, no 'accidental' likes, no asking her friends.",
              "Hide, mute, archive — not block. Blocking is still attention.",
              "If you slip, reset the clock. That's data, not a verdict.",
              "The goal is becoming a man you'd come back to — not winning her back.",
            ].map((r, i) => (
              <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-500 mt-1 shrink-0"/> {r}</li>
            ))}
          </ul>

          <div className="divider-thin my-8" />

          <label className="overline text-neutral-500">A sentence or two of context (optional)</label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            placeholder="e.g. She ended it last week. We dated 18 months. Lived together the last 6."
            className="w-full mt-2 mb-4 p-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
            data-testid="recovery-context-input"
          />
          <label className="overline text-neutral-500">Length of relationship (optional)</label>
          <input
            type="text"
            value={length}
            onChange={(e) => setLength(e.target.value)}
            placeholder="e.g. 2 years"
            className="w-full mt-2 mb-6 px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
            data-testid="recovery-length-input"
          />

          <button onClick={start} disabled={starting} className="btn-amber inline-flex items-center gap-2 disabled:opacity-60" data-testid="recovery-start-btn">
            {starting ? "Starting…" : "Start Day 1 now"} <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </div>
    );
  }

  // Active program
  const { current_day, phase, today, completed_days, all_days, ready_to_reach_out } = data;
  const completedSet = new Set(completed_days);
  const pct = Math.round((completedSet.size / 30) * 100);
  const dayCircles = Array.from({ length: 30 }, (_, i) => i + 1);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start gap-4 mb-6">
        <HeartCrack className="w-7 h-7 text-amber-500 shrink-0 mt-1" />
        <div className="flex-1">
          <div className="overline mb-1">BREAKUP RECOVERY · ACTIVE</div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
            Day <span className="italic text-amber-500">{current_day}</span> of 30
          </h1>
          <div className="overline text-neutral-500 mt-2">{phase.toUpperCase()}</div>
        </div>
        <button onClick={() => setConfirmReset(true)} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="recovery-reset-btn">
          <RotateCcw className="w-4 h-4"/> Reset
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="overline text-neutral-500">Tasks completed</span>
          <span className="text-amber-400" data-testid="recovery-progress">{completedSet.size} / 30</span>
        </div>
        <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} className="h-full bg-amber-500" />
        </div>
      </div>

      {/* Today's task */}
      <div className="mt-8 card-surface p-8 relative overflow-hidden" data-testid="today-task-card">
        <div className="absolute top-0 right-0 w-1 h-full bg-amber-500/60"/>
        <div className="overline text-amber-500 mb-3 flex items-center gap-2"><Flame className="w-3.5 h-3.5"/> TODAY · DAY {today.day}</div>
        <h2 className="font-serif text-3xl md:text-4xl mb-2">{today.title}</h2>
        <p className="text-neutral-300 leading-relaxed mb-5">{today.task}</p>
        <div className="card-elevated p-4 border-l-2 border-amber-500 mb-6">
          <div className="overline text-amber-500 mb-1 inline-flex items-center gap-2"><Lightbulb className="w-3.5 h-3.5"/> REFLECTION</div>
          <p className="font-serif text-xl text-neutral-200">"{today.reflection}"</p>
        </div>
        <button
          onClick={() => toggleDay(today.day, !completedSet.has(today.day))}
          data-testid="today-complete-btn"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border transition-colors ${
            completedSet.has(today.day)
              ? "bg-amber-500/10 border-amber-500/40 text-amber-400"
              : "btn-amber"
          }`}
        >
          {completedSet.has(today.day) ? <><Check className="w-4 h-4"/> Done today</> : "Mark done"}
        </button>
      </div>

      {/* Ready to reach out banner */}
      {ready_to_reach_out && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 card-surface p-6 flex items-start md:items-center justify-between gap-4 flex-wrap border-amber-500/40"
          data-testid="reach-out-ready-banner"
        >
          <div className="flex-1 min-w-[260px]">
            <div className="overline text-amber-500 mb-2 flex items-center gap-2"><Target className="w-3.5 h-3.5"/> 30 DAYS COMPLETE</div>
            <h3 className="font-serif text-2xl">You've earned the right to decide.</h3>
            <p className="text-neutral-400 text-sm mt-2 italic">Send one short, specific message — or don't. Both are winning moves.</p>
          </div>
          <Link to="/tools?tab=reconnect" className="btn-amber inline-flex items-center gap-2" data-testid="go-reconnect-btn">
            <MessageCircle className="w-4 h-4"/> Draft a reconnect message
          </Link>
        </motion.div>
      )}

      {/* 30-day grid */}
      <div className="mt-10">
        <h2 className="font-serif text-2xl mb-5">The 30 days</h2>
        <div className="grid grid-cols-10 sm:grid-cols-15 gap-2 mb-6" data-testid="day-grid">
          {dayCircles.map((d) => {
            const locked = d > current_day;
            const done = completedSet.has(d);
            const isToday = d === current_day;
            return (
              <div
                key={d}
                data-testid={`day-circle-${d}`}
                className={`aspect-square flex items-center justify-center text-xs rounded-lg border ${
                  isToday
                    ? "border-amber-500 bg-amber-500/15 text-amber-400 font-semibold"
                    : done
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
                      : locked
                        ? "border-white/5 bg-white/[0.02] text-neutral-700"
                        : "border-white/10 bg-white/[0.03] text-neutral-400"
                }`}
                title={`Day ${d}`}
              >
                {locked ? <Lock className="w-3 h-3"/> : done ? <Check className="w-3 h-3"/> : d}
              </div>
            );
          })}
        </div>

        <div className="space-y-3">
          {all_days.filter((t) => t.day <= current_day).reverse().map((t) => (
            <div key={t.day} className="card-surface p-5 flex items-start gap-4" data-testid={`day-item-${t.day}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-serif ${
                completedSet.has(t.day) ? "bg-amber-500/15 text-amber-400 border border-amber-500/40" : "bg-white/5 text-neutral-400 border border-white/10"
              }`}>
                {t.day}
              </div>
              <div className="flex-1">
                <div className="overline text-neutral-500 mb-1">{t.phase}</div>
                <div className="font-serif text-lg">{t.title}</div>
                <p className="text-neutral-400 text-sm mt-1 leading-relaxed">{t.task}</p>
              </div>
              <button
                onClick={() => toggleDay(t.day, !completedSet.has(t.day))}
                data-testid={`toggle-day-${t.day}`}
                className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
                  completedSet.has(t.day)
                    ? "bg-amber-500 border-amber-500 text-white"
                    : "border-white/10 text-neutral-400 hover:text-white hover:border-amber-500/40"
                }`}
                title="Toggle done"
              >
                <Check className="w-4 h-4"/>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Confirm reset modal */}
      <AnimatePresence>
        {confirmReset && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50"
            onClick={() => setConfirmReset(false)}
          >
            <div className="card-surface p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="reset-modal">
              <AlertTriangle className="w-8 h-8 text-amber-500 mb-3"/>
              <h3 className="font-serif text-2xl mb-2">Reset the program?</h3>
              <p className="text-neutral-400 mb-6 text-sm">If you broke no-contact, resetting is the honest move. The clock starts over at Day 1 — and that's fine.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmReset(false)} className="text-sm text-neutral-500 hover:text-white px-3">Cancel</button>
                <button onClick={reset} className="btn-amber text-sm" data-testid="confirm-reset-btn">Yes, reset Day 1</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExRecovery;
